import { useRef, useState } from 'react';
import {
  DraftSaveFlushError,
  WEBSITE_INTERFACE_LOCALES,
  useSetWebsiteInterfaceLocaleV3,
  type DraftSaveQueue,
  type SupabaseClient,
  type WebsiteInterfaceLocale,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useWebsiteT } from '../hooks/use-website-t';
import { isStaleDraft, saveErrorKey } from '../lib/issue-messages';

export interface StoreLanguageDialogProps {
  readonly client: SupabaseClient;
  readonly storeId: string;
  /** Epic #228 P3: the store language is saved into this template's draft. */
  readonly templateId: string;
  readonly current: WebsiteInterfaceLocale;
  /**
   * Epic #228 P4 (PD10): the template's auto-save queue. The change waits for
   * the queue to save, writes on the queue's base and hands the new head back,
   * so it never races a text edit.
   */
  readonly queue: DraftSaveQueue;
  /** Asks the server for this template's draft again (after a change made elsewhere). */
  readonly reloadState: () => Promise<unknown>;
  readonly onClose: () => void;
  readonly newMutationId?: () => string;
}

type Failure = 'flush' | 'stale' | 'other';

/**
 * A4: the language of the buttons, cart, checkout and messages of the store.
 * The merchant's own texts stay exactly as written, and the language of this
 * dashboard does not change.
 */
export function StoreLanguageDialog({
  client,
  storeId,
  templateId,
  current,
  queue,
  reloadState,
  onClose,
  newMutationId = () => crypto.randomUUID(),
}: StoreLanguageDialogProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const [locale, setLocale] = useState<WebsiteInterfaceLocale>(current);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [busy, setBusy] = useState(false);
  const mutation = useSetWebsiteInterfaceLocaleV3(client, storeId);
  // A retry of the same change on the same head reuses its id: the server replays the first result.
  const mutationRef = useRef<{
    readonly id: string;
    readonly locale: WebsiteInterfaceLocale;
    readonly base: string;
  } | null>(null);

  // Save is disabled while the chosen language is the current one.
  const save = async (): Promise<void> => {
    setErrorKey(null);
    setFailure(null);
    setBusy(true);
    await apply();
  };

  /** Writes the chosen language on the queue's base, then closes; says why when it cannot. */
  const apply = async (): Promise<void> => {
    try {
      await queue.runExclusive(async (base) => {
        const previous = mutationRef.current;
        const attempt =
          previous !== null && previous.locale === locale && previous.base === base
            ? previous
            : { id: newMutationId(), locale, base };
        mutationRef.current = attempt;
        const result = await mutation.mutateAsync({
          templateId,
          locale,
          expectedHeadRevisionId: base,
          clientMutationId: attempt.id,
        });
        return { result, head: { revisionId: result.draftRevisionId } };
      });
      onClose();
    } catch (error) {
      if (error instanceof DraftSaveFlushError) {
        setFailure('flush');
        setErrorKey('admin.websiteTemplates.publish.flushFirst');
      } else if (isStaleDraft(error)) {
        setFailure('stale');
        setErrorKey('admin.websiteTemplates.dialog.conflictBody');
      } else {
        setFailure('other');
        setErrorKey(saveErrorKey(error));
      }
    } finally {
      setBusy(false);
    }
  };

  // The draft changed elsewhere: take the newer draft (the queue's base too),
  // then apply the merchant's choice on top of it (fix round 2, same rule as
  // slot values in PD9), so "your choice is kept" is true.
  const reload = async (): Promise<void> => {
    setBusy(true);
    let reloadErrorKey: string | null = null;
    try {
      const reloaded = await queue.reload();
      if (reloaded.ok) {
        await reloadState();
      } else {
        // The newer head could not be taken (fetch failed) or the merchant's
        // queued edits could not be saved on top of it: stay, say so, keep Reload.
        reloadErrorKey = queue.getSnapshot().hasFailedPayload
          ? 'admin.websiteTemplates.publish.flushFirst'
          : 'admin.websiteTemplates.errors.generic';
      }
    } catch {
      reloadErrorKey = 'admin.websiteTemplates.errors.generic';
    }
    if (reloadErrorKey !== null) {
      setErrorKey(reloadErrorKey);
      setBusy(false);
      return;
    }
    setErrorKey(null);
    setFailure(null);
    await apply();
  };

  return (
    // Always open while mounted: Radix only ever reports a close (X, Escape, outside).
    <Dialog
      open
      onOpenChange={() => {
        if (!busy) onClose();
      }}
    >
      <DialogContent data-testid="store-language-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.websiteTemplates.locale.title')}</DialogTitle>
          <DialogDescription>{t('admin.websiteTemplates.locale.help')}</DialogDescription>
        </DialogHeader>
        <fieldset className="space-y-2" disabled={busy}>
          <legend className="sr-only">{t('admin.websiteTemplates.locale.title')}</legend>
          {WEBSITE_INTERFACE_LOCALES.map((code) => (
            <label
              key={code}
              className="border-border hover:bg-admin-surface-muted flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm"
            >
              <input
                type="radio"
                name="store-language"
                value={code}
                checked={locale === code}
                onChange={() => setLocale(code)}
                className="accent-admin-lime size-4"
              />
              <span lang={code}>{t(`admin.websiteTemplates.locale.${code}`)}</span>
            </label>
          ))}
        </fieldset>
        <p className="text-muted-foreground text-xs">
          {t('admin.websiteTemplates.locale.independent')}
        </p>
        {errorKey === null ? null : (
          <p role="alert" data-testid="store-language-error" className="text-destructive text-sm">
            {t(errorKey)}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
            {t('admin.websiteTemplates.common.cancel')}
          </Button>
          {failure === 'stale' ? (
            <Button
              type="button"
              disabled={busy}
              data-testid="store-language-reload"
              onClick={() => void reload()}
            >
              {t('admin.websiteTemplates.dialog.reloadDraft')}
            </Button>
          ) : (
            <Button
              type="button"
              disabled={locale === current || busy}
              data-testid="store-language-save"
              onClick={() => void save()}
            >
              {busy
                ? t('admin.websiteTemplates.locale.saving')
                : failure === 'flush'
                  ? t('admin.websiteTemplates.common.retry')
                  : t('admin.websiteTemplates.locale.save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
